import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n from '../../i18n/i18n';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Helper function to get current language
const getCurrentLanguage = () => {
    return i18n.language || 'ar';
};

// Helper function to get auth token from sessionStorage
const getAuthToken = () => {
    const storeData = sessionStorage.getItem('storekhadamat');
    if (storeData) {
        try {
            const parsed = JSON.parse(storeData);
            return parsed.token;
        } catch {
            return null;
        }
    }
    return null;
};

// Get Store Branches
export const getStoreBranches = createAsyncThunk(
    'storeBranches/getStoreBranches',
    async (_, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const token = getAuthToken();
            
            if (!token) {
                return rejectWithValue('لا يوجد token للمصادقة');
            }

            const response = await axios.get(`${BASE_URL}/stores/store_branches`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': currentLang,
                    'lang': currentLang,
                },
            });
            
            if (response.data.status === 'failed' || response.data.status === 'faild') {
                return rejectWithValue(response.data.message || 'فشل في جلب فروع المتجر');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في جلب فروع المتجر');
        }
    }
);

// Add Store Branch
export const addStoreBranch = createAsyncThunk(
    'storeBranches/addStoreBranch',
    async (branchData, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const token = getAuthToken();
            
            if (!token) {
                return rejectWithValue('لا يوجد token للمصادقة');
            }

            // تحديد headers بناءً على نوع البيانات
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Accept-Language': currentLang,
                'lang': currentLang,
            };

            // إذا كانت البيانات FormData، لا نحدد Content-Type (axios سيحدده تلقائياً)
            // إذا كانت object عادي، نحدد application/json
            if (!(branchData instanceof FormData)) {
                headers['Content-Type'] = 'application/json';
            }

            const response = await axios.post(`${BASE_URL}/stores/add_branch`, branchData, {
                headers: headers,
            });
            
            if (response.data.status === 'failed' || response.data.status === 'faild') {
                return rejectWithValue(response.data.message || 'فشل في إضافة الفرع');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في إضافة الفرع');
        }
    }
);

// Update Store Branch
export const updateStoreBranch = createAsyncThunk(
    'storeBranches/updateStoreBranch',
    async ({ branchId, branchData }, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const token = getAuthToken();
            
            if (!token) {
                return rejectWithValue('لا يوجد token للمصادقة');
            }

            // تحديد headers بناءً على نوع البيانات
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Accept-Language': currentLang,
                'lang': currentLang,
            };

            // إذا كانت البيانات FormData، لا نحدد Content-Type (axios سيحدده تلقائياً)
            // إذا كانت object عادي، نحدد application/json
            if (!(branchData instanceof FormData)) {
                headers['Content-Type'] = 'application/json';
            }

            const response = await axios.post(`${BASE_URL}/stores/store_branches/${branchId}`, branchData, {
                headers: headers,
            });
            
            if (response.data.status === 'failed' || response.data.status === 'faild') {
                return rejectWithValue(response.data.message || 'فشل في تحديث الفرع');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في تحديث الفرع');
        }
    }
);

// Delete Store Branch
export const deleteStoreBranch = createAsyncThunk(
    'storeBranches/deleteStoreBranch',
    async (branchId, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const token = getAuthToken();
            
            if (!token) {
                return rejectWithValue('لا يوجد token للمصادقة');
            }

            const response = await axios.delete(`${BASE_URL}/stores/store_branches/${branchId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': currentLang,
                    'lang': currentLang,
                },
            });
            
            if (response.data.status === 'failed' || response.data.status === 'faild') {
                return rejectWithValue(response.data.message || 'فشل في حذف الفرع');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في حذف الفرع');
        }
    }
);

// Get Branch Details
export const getBranchDetails = createAsyncThunk(
    'storeBranches/getBranchDetails',
    async (branchId, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const token = getAuthToken();
            
            if (!token) {
                return rejectWithValue('لا يوجد token للمصادقة');
            }

            const response = await axios.get(`${BASE_URL}/stores/store_branches/${branchId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': currentLang,
                    'lang': currentLang,
                },
            });
            
            if (response.data.status === 'failed' || response.data.status === 'faild') {
                return rejectWithValue(response.data.message || 'فشل في جلب تفاصيل الفرع');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في جلب تفاصيل الفرع');
        }
    }
);

const initialState = {
    branches: [],
    pagination: null,
    loading: false,
    error: null,
    successMessage: null
};

const storeBranchesSlice = createSlice({
    name: 'storeBranches',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearBranches: (state) => {
            state.branches = [];
        }
    },
    extraReducers: (builder) => {
        builder
            // Get Store Branches
            .addCase(getStoreBranches.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getStoreBranches.fulfilled, (state, action) => {
                state.loading = false;
                const branchesData = action.payload?.result?.data || action.payload?.data || action.payload || [];
                state.branches = Array.isArray(branchesData) ? branchesData : [];
                state.pagination = action.payload?.result?.pagination || action.payload?.pagination || null;
                state.error = null;
            })
            .addCase(getStoreBranches.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Add Store Branch
            .addCase(addStoreBranch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addStoreBranch.fulfilled, (state, action) => {
                state.loading = false;
                // إضافة الفرع الجديد للقائمة
                if (action.payload.result) {
                    state.branches.push(action.payload.result);
                }
                state.error = null;
                state.successMessage = action.payload.message || 'تم إضافة الفرع بنجاح';
            })
            .addCase(addStoreBranch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Update Store Branch
            .addCase(updateStoreBranch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateStoreBranch.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.successMessage = action.payload.message || 'تم تحديث الفرع بنجاح';
                // تحديث الفرع في القائمة
                if (action.payload.result) {
                    const index = state.branches.findIndex(branch => branch.id === action.payload.result.id);
                    if (index !== -1) {
                        state.branches[index] = action.payload.result;
                    }
                }
            })
            .addCase(updateStoreBranch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Delete Store Branch
            .addCase(deleteStoreBranch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteStoreBranch.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.successMessage = action.payload.message || 'تم حذف الفرع بنجاح';
                // حذف الفرع من القائمة
                if (action.payload.branchId) {
                    state.branches = state.branches.filter(branch => branch.id !== action.payload.branchId);
                }
            })
            .addCase(deleteStoreBranch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Get Branch Details
            .addCase(getBranchDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getBranchDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                // يمكن إضافة branchDetails للـ state إذا لزم الأمر
            })
            .addCase(getBranchDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { clearError, clearSuccessMessage, clearBranches } = storeBranchesSlice.actions;

export default storeBranchesSlice.reducer;
